import { CdkAccordionModule } from '@angular/cdk/accordion';
import { Dialog } from '@angular/cdk/dialog';
import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ContractsDataService, ContractStatus, EaglesoftPasswordStatus, ProfileSite, SiteType } from '@ffd/api-client';
import { ToastService, TokenService } from '@libs/shared-services';
import { selectEaglesoftCredentialStatuses, selectUserSiteId } from '@libs/store';
import { AvatarInitialsComponent, InputComponent, NotificationDotComponent } from '@libs/ui-lib';
import { ffdNotesRoutePrefix, isFfdNotesContext, PmsDisplayCodePipe, routes } from '@libs/utils';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import packageJson from '../../../../../package.json';
import { DeleteAccountModalComponent, DeleteAccountModalComponentResult } from './components/delete-account-modal';
import { EaglesoftPasswordPromptService } from './components/eaglesoft-password-modal';
import { MyAccountFormGroup } from './my-account-screen.interface';
import { MyAccountScreenService } from './my-account-screen.service';

@Component({
  selector: 'lib-my-account',
  imports: [
    CommonModule,
    AvatarInitialsComponent,
    InputComponent,
    ReactiveFormsModule,
    SvgIconComponent,
    TranslateModule,
    CdkAccordionModule,
    PmsDisplayCodePipe,
    NotificationDotComponent,
  ],
  templateUrl: './my-account-screen.component.html',
  styleUrl: './my-account-screen.component.scss',
  providers: [MyAccountScreenService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.ffd-notes]': 'isFfdNotes',
  },
})
export class MyAccountScreenComponent implements OnInit {
  private readonly myAccountService = inject(MyAccountScreenService);
  private readonly tokenService = inject(TokenService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly contractsDataService = inject(ContractsDataService);
  private readonly dialog = inject(Dialog);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly eaglesoftPasswordPromptService = inject(EaglesoftPasswordPromptService);

  protected readonly appVersion = packageJson.version;
  private readonly canGoBack: boolean = false;

  private readonly selectedSiteId = this.store.selectSignal(selectUserSiteId)();

  protected readonly profile = computed(() => this.myAccountService.viewModel().profile);
  protected readonly isEditingName = signal(false);

  protected readonly practiceInfo = computed(() => {
    const profile = this.profile();
    if (!profile) return null;
    if (this.selectedSiteId) {
      const site = profile.sites.find((s) => s.id === this.selectedSiteId);
      if (site) return site;
    }
    return profile.sites.find((s) => s.siteType === SiteType.FFD_NOTES) ?? null;
  });

  protected readonly eaglesoftCredentialStatuses = this.store.selectSignal(selectEaglesoftCredentialStatuses);

  protected readonly signedContractUrl = signal<string | null>(null);
  protected readonly isInviteLinkLoading = computed(() => this.myAccountService.viewModel().isInviteLinkLoading);
  public isFfdNotes = isFfdNotesContext(this.router.url);

  protected readonly profileForm = this.formBuilder.group<MyAccountFormGroup>({
    firstName: new FormControl<string | null>(null, [Validators.required]),
    lastName: new FormControl<string | null>(null, [Validators.required]),
    email: new FormControl<string | null>(null),
    phone: new FormControl<string | null>(null),
    // role: new FormControl<string | null>(null),
  });

  public ngOnInit(): void {
    this.profileForm.disable();
    this.myAccountService.init();
    this.updateFormWithProfile();
  }

  constructor() {
    this.canGoBack = !!this.router.getCurrentNavigation()?.previousNavigation;
    effect(() => {
      const notesSite = this.practiceInfo();
      if (!notesSite?.isContractSignedByCurrentUser) return;
      const signedContract = this.profile()?.signedContracts?.find(
        (c) => c.siteId === notesSite.id && c.notesContractStatus === ContractStatus.SIGNED,
      );
      if (signedContract) {
        this.contractsDataService.downloadSignedPdf(signedContract.id).subscribe({
          next: (response) => this.signedContractUrl.set(response.url),
          error: () => this.signedContractUrl.set(null),
        });
      }
    });
  }

  protected logoutClick(): void {
    this.tokenService.logout();
  }

  protected onBackClick(): void {
    if (this.canGoBack) {
      this.location.back();
    } else {
      this.router.navigate(this.isFfdNotes ? ffdNotesRoutePrefix() : [routes.home]);
    }
  }

  private updateFormWithProfile(): void {
    const profile = this.profile();
    if (profile) {
      this.profileForm.patchValue({
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        // role: profile.role,
      });
    }
  }

  protected onEditNameClick(): void {
    this.isEditingName.set(true);
    this.profileForm.controls.firstName.enable();
    this.profileForm.controls.lastName.enable();
  }

  protected onSaveNameClick(): void {
    const firstName = this.profileForm.controls.firstName.value;
    const lastName = this.profileForm.controls.lastName.value;

    if (firstName && lastName && this.profileForm.controls.firstName.valid && this.profileForm.controls.lastName.valid) {
      this.myAccountService.updateProfileName({ firstName, lastName });
      this.isEditingName.set(false);
      this.profileForm.controls.firstName.disable();
      this.profileForm.controls.lastName.disable();
    }
  }

  protected onCancelNameEdit(): void {
    this.updateFormWithProfile(); // Reset to original values
    this.isEditingName.set(false);
    this.profileForm.controls.firstName.disable();
    this.profileForm.controls.lastName.disable();
  }

  protected onNameKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSaveNameClick();
    } else if (event.key === 'Escape') {
      this.onCancelNameEdit();
    }
  }

  protected onSupportClick(): void {
    this.router.navigate(['/', routes.site, this.selectedSiteId ?? routes.showcaseSiteId, routes.supportCenter]);
  }

  protected onShareInviteClick(): void {
    const siteId = this.practiceInfo()?.id;
    if (!siteId || this.isInviteLinkLoading()) return;

    this.myAccountService.shareInviteLink(siteId);
  }

  protected isEaglesoftSite(site: ProfileSite): boolean {
    return this.eaglesoftCredentialStatuses().some((status) => status.siteId === site.id);
  }

  protected hasEaglesoftPasswordIssue(site: ProfileSite): boolean {
    return this.eaglesoftCredentialStatuses().some(
      (status) => status.siteId === site.id && status.status === EaglesoftPasswordStatus.INVALID,
    );
  }

  protected onSetPasswordClick(site: ProfileSite): void {
    // The success toast belongs to the prompt service, which owns the single shared dialog.
    this.eaglesoftPasswordPromptService.prompt(site.id).subscribe();
  }

  protected onRequestDeleteAccountClick(): void {
    this.dialog.open<DeleteAccountModalComponentResult>(DeleteAccountModalComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[360px]'],
      disableClose: true,
    });
  }
}
